import { RADIO_GROUP_STATE, add_radio_observer, remove_radio_observer } from './radio_state.js';
import {YalwaRadioBase} from './radio_base';
import css_text from '/srv/www/htdocs/loc/app/styles/web_components/radio_group.css';

const ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><circle cx="5" cy="5" r="5"/></svg>`;
let unique_groupID_counter = 0; // local fallback if not provided elsewhere

export class YalwaRadioOption extends YalwaRadioBase {
	
	static get observedAttributes() {
		return ['intent', 'disabled', 'checked', 'value', 'mark_required', 'group-id', 'name'];
	}
	
	constructor() {
		super();
		if (!this.shadowRoot) {
			this.attachShadow({mode: 'open'});
		}
		this.addEventListener('click', this.on_click);
		this.addEventListener('keydown', this.on_keydown);
		this.handle_radio_observation = this.handle_radio_observation.bind(this);
	}
	

	
	initialize_template() {
		if (!this.shadowRoot.firstChild) {
			const template = document.createElement('template');
			template.innerHTML = `
                <div class="wrapper" part="wrapper">
                    <span class="box" part="box"><slot name="icon">${ICON}</slot></span>
                    <span class="label" part="label"><slot></slot></span>
                </div>`;
			this.shadowRoot.appendChild(template.content.cloneNode(true));
		}
		this.core_element = this.shadowRoot.querySelector('.wrapper');
		this.box_element = this.shadowRoot.querySelector('.box');
	}
	
	async connectedCallback() {
		if (!this._connected_once) {
			this._connected_once = true;
			try {
				await this.load_css(css_text);          // wait for CSS
				this.initialize_template();     // then initialize template
			} catch (err) {
				console.error('Failed to load radio CSS:', err);
				this.initialize_template();     // fallback
			}
		}

		if (!this.hasAttribute('tabindex')) {
			this.setAttribute('tabindex', '0');
		}
		
		this.upgrade_properties(['intent','checked','value','disabled','mark_required','group-id','name']);
		
		this.groupID = this.getAttribute('group-id');
		if (!this.groupID && this.getAttribute('name')) {
			this.groupID = `ywc-radio-${this.getAttribute('name')}-${++unique_groupID_counter}`;
			this.setAttribute('group-id', this.groupID);
		}
		
		if (this.groupID) {
			add_radio_observer(this.groupID, this.handle_radio_observation);
		}
		
		this.initial_sync();
		this.render();
	}
	
	disconnectedCallback() {
		if (this.groupID) {
			remove_radio_observer(this.groupID, this.handle_radio_observation);
		}
	}
	
	attributeChangedCallback(name, oldV, newV) {
		if (oldV === newV) {
			return;
		}
		if (name === 'disabled') {
			this.setAttribute('tabindex', newV !== null ? '-1' : '0');
		}
		this.render();
	}
	
	render() {
		if (!this.core_element || !this.box_element) {
			return;
		}
		this.core_element.toggleAttribute('data-mark_required', this.mark_required);
		this.core_element.setAttribute('data-intent', this.getAttribute('intent') || 'default');
		this.box_element.style.display = 'inline-flex';
		this.core_element.setAttribute('data-state', this.checked ? 'checked' : 'unchecked');
		this.core_element.toggleAttribute('data-disabled', this.disabled);
	}
	
	initial_sync() {
		const group_selected = RADIO_GROUP_STATE[this.groupID];
		const has_checked_attr = this.hasAttribute('checked');
		const value = this.getAttribute('value');
		
		if (group_selected !== undefined) {
			this.checked = (value === group_selected);
			return;
		}
		if (has_checked_attr) {
			RADIO_GROUP_STATE[this.groupID] = value;
			this.checked = true;
		} else {
			this.checked = false;
		}
	}
	
	handle_radio_observation({ selected_value }) {
		this.checked = this.value === selected_value;
	}
	
	// --- Props
	get checked() {
		return this.hasAttribute('checked');
	}
	set checked(val) {
		if (val) {
			this.setAttribute('checked', '');
		} else {
			this.removeAttribute('checked');
		}
	}
	
	get value() {
		return this.getAttribute('value');
	}
	set value(v) {
		this.setAttribute('value', v);
	}
	
	get disabled() {
		return this.hasAttribute('disabled');
	}
	set disabled(v) {
		if (v) {
			this.setAttribute('disabled', '');
		} else {
			this.removeAttribute('disabled');
		}
	}
	
	// --- Interaction
	on_click(e) {
		if (this.disabled) {
			return;
		}
		this.toggle();
	}
	on_keydown(e) {
		if (this.disabled) {
			return;
		}
		if (e.key === ' ') {
			e.preventDefault();
			this.toggle();
		}
	}
	
	toggle() {
		if (this.disabled) {
			return;
		}
		if (!this.groupID) {
			return;
		}
		if (this.checked) {
			return;
		}
		RADIO_GROUP_STATE[this.groupID] = this.value;
		this.checked = true;
		this._internals?.setFormValue(this.value);
	}
}

if (!customElements.get('yalwa-radio-option')) {
	customElements.define('yalwa-radio-option', YalwaRadioOption);
}